import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Styles from './Dropzone.module.scss';

/**
 * Icone placeholder para upload de arquivo.
 *
 * @returns {JSX.Element}
 * @constructor
 */
const DropzoneIcon = () => {
  return (
    <svg viewBox={'0 0 32 32'}>
      <rect
        x={0}
        y={0}
        width={32}
        height={32}
        fill={'rgba(255, 255, 255, 0)'}
      />
      <path
        d={
          'M27.667,17.537l-7,0l-0,-1.555l3.111,-0c2.041,-0 3.111,-2.479 1.604,-3.938l-7.778,-7.778c-0.875,-0.923 -2.382,-0.923 -3.257,0l-7.778,7.778c-1.506,1.459 -0.437,3.938 1.653,3.938l3.111,-0l0,1.555l-7,0c-1.312,0 -2.333,1.07 -2.333,2.334l0,6.222c0,1.312 1.021,2.333 2.333,2.333l23.334,0c1.264,0 2.333,-1.021 2.333,-2.333l-0,-6.222c-0,-1.264 -1.069,-2.334 -2.333,-2.334Zm-19.445,-3.889l7.778,-7.777l7.778,7.777l-5.445,0l0,7.778l-4.666,0l-0,-7.778l-5.445,0Zm19.445,12.445l-23.334,-0l0,-6.222l7,-0l0,1.555c0,1.313 1.021,2.334 2.334,2.334l4.666,-0c1.264,-0 2.334,-1.021 2.334,-2.334l-0,-1.555l7,-0l-0,6.222Zm-1.945,-3.111c0,-0.632 -0.535,-1.167 -1.166,-1.167c-0.681,0 -1.167,0.535 -1.167,1.167c-0,0.68 0.486,1.166 1.167,1.166c0.631,0 1.166,-0.486 1.166,-1.166Z'
        }
        fill={'#c0c0c0'}
      />
    </svg>
  );
};

/**
 * Define um drop zone para envio de arquivos.
 *
 * Executa tanto no evento de `onClick` quanto no de `onDrop`.
 *
 * `onDrop` é um callback que receberá como parâmetro um array com os arquivos
 * para upload (cada arquivo é uma instância de `File`).
 *
 * @param id
 * @param className
 * @param children
 * @param disabled
 * @param disabledClassName
 * @param highlightClassName
 * @param onDrop
 * @param multiple
 * @returns {JSX.Element}
 * @constructor
 */

const imagesType = [
  'image/apng',
  'image/bmp',
  'image/gif',
  'image/jpeg',
  'image/pjpeg',
  'image/png',
  'image/svg+xml',
  'image/tiff',
  'image/webp',
  'image/x-icon',
];

const applicationsType = ['application/pdf'];

export const Dropzone = ({
  id,
  className,
  children,
  disabled,
  disabledClassName,
  highlightClassName,
  onDrop,
  multiple,
  openExplorer,
  isEmptyInputFileAndNotClicked,
  acceptFilesType = [...imagesType, ...applicationsType],
}) => {
  const [highlight, setHighlight] = useState(false);
  const [message, setMessage] = useState(null);

  /**
   * @type {React.MutableRefObject<HTMLInputElement>}
   */

  const fileHandleRef = useRef(null);

  // Events
  // --------------------------------------------------------------------

  /**
   * @param {InputEvent} evt
   */
  const onDragLeave = evt => {
    evt.preventDefault();
    setHighlight(false);
  };

  /**
   * @param {InputEvent} evt
   */
  const onDragOver = evt => {
    evt.preventDefault();
    if (disabled) return;
    setHighlight(true);
  };

  /**
   * @param {DragEvent} evt
   */
  const onDropFiles = evt => {
    evt.preventDefault();
    if (disabled) return;
    const { files } = evt.dataTransfer;

    if (!files instanceof FileList || files.length < 1) {
      setHighlight(false);
      return;
    }

    const acceptFiles = validateAcceptFileTypes(files);

    if (onDrop) {
      if (acceptFiles.length === 0) {
        setHighlight(false);
        return;
      }

      onDrop(toFileArray(acceptFiles));
    }
  };

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} evt
   */
  const onFilesAdded = evt => {
    evt.preventDefault();
    if (disabled) return;
    const { files } = evt.target;

    const acceptFiles = validateAcceptFileTypes(files) ?? [];

    if (onDrop) {
      if (acceptFiles.length === 0) {
        setHighlight(false);
        return;
      }

      onDrop(toFileArray(acceptFiles));
    }
  };

  const validateAcceptFileTypes = (files = []) => {
    let serializedFiles = Array.from(files);

    if (serializedFiles.length > 0) {
      serializedFiles = serializedFiles.filter(file =>
        acceptFilesType.includes(file.type)
      );

      return serializedFiles;
    }
  };

  /**
   * Abre a caixa de seleção de arquivos num clique, usa o ref do input de
   * arquivos.
   */
  const openDialog = useCallback(() => {
    if (disabled) return;
    fileHandleRef.current.click();
  }, [disabled]);

  useEffect(() => {
    if (isEmptyInputFileAndNotClicked) {
      fileHandleRef.current.click();
    }
  }, [isEmptyInputFileAndNotClicked, openExplorer]);

  /**
   * @returns {JSX.Element|*}
   */
  const renderChildren = () => {
    if (!children) {
      return (
        <div>
          <DropzoneIcon />
          <p>Clique aqui ou arraste arquivos para envio.</p>
        </div>
      );
    }

    return children;
  };

  /**
   * @param {FileList} files
   * @returns {File[]}
   */
  const toFileArray = files => {
    const array = [];
    for (let n = 0; n < files.length; n++) {
      array.push(files[n]);
    }
    return array;
  };

  // --------------------------------------------------------------------

  const _attr = {};
  if (id) _attr.id = id;
  _attr.className = classnames(className, {
    [highlightClassName]: highlight,
    [disabledClassName]: disabled,
  });

  return (
    <div
      {..._attr}
      onClick={openDialog}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDropFiles}
    >
      {renderChildren()}

      <input
        type={'file'}
        accept={acceptFilesType}
        ref={fileHandleRef}
        onChange={onFilesAdded}
        multiple={multiple}
      />
    </div>
  );
};

Dropzone.defaultProps = {
  id: null,
  className: Styles['dropzone'],
  children: null,
  disabled: false,
  disabledClassName: Styles['disabled'],
  highlightClassName: Styles['highlight'],
  onDrop: null,
  multiple: true,
};

Dropzone.propTypes = {
  id: PropTypes.string,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.objectOf(PropTypes.bool),
  ]),
  children: PropTypes.node,
  disabled: PropTypes.bool,
  disabledClassName: PropTypes.string,
  highlightClassName: PropTypes.string,
  onDrop: PropTypes.func,
  multiple: PropTypes.bool,
};
